import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Post.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002";

const getImageSrc = (imageUrl) => {
  if (!imageUrl) return "";
  return imageUrl.startsWith("http") ? imageUrl : `${API_URL}${imageUrl}`;
};

const Post = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [commentForm, setCommentForm] = useState({
    name: "",
    message: "",
  });

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("adminToken") ||
    localStorage.getItem("clientToken") ||
    localStorage.getItem("chauffeurToken");

  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: false,
      mirror: true,
    });
  }, []);

  const fetchPost = useCallback(async () => {
  try {
    const res = await axios.get(`${API_URL}/api/posts/${id}`);
    setPost(res.data);

    setTimeout(() => {
      AOS.refresh();
    }, 100);
  } catch (error) {
    console.error("Erreur chargement article :", error);
  }
}, [id]);

useEffect(() => {
  fetchPost();
}, [fetchPost]);

  const handleLike = async () => {
    if (!token) return;

    try {
      const res = await axios.post(
        `${API_URL}/api/posts/${id}/like`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPost((prev) => ({
        ...prev,
        likes: res.data.likes,
      }));
    } catch (error) {
      console.error("Erreur like :", error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!token) return;

    try {
      await axios.post(
        `${API_URL}/api/posts/${id}/comment`,
        commentForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCommentForm({ name: "", message: "" });
      fetchPost();
    } catch (error) {
      console.error("Erreur commentaire :", error);
    }
  };

  const shareUrl = window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(post?.title || "");

  if (!post) {
    return (
      <div className="post-empty-state" data-aos="fade-up">
        <h2>Chargement...</h2>
      </div>
    );
  }

  return (
    <div className="single-post-page">
      <Link to="/" className="post-back-link" data-aos="fade-right">
        ← Retour à l’accueil
      </Link>

      <div className="single-post-card" data-aos="fade-up">
        <div className="single-post-header">
          <span className="post-top-badge">Actualité</span>
          <h1 className="single-post-title">{post.title}</h1>
        </div>

        {post.imageUrl && (
          <div className="single-post-image-wrap" data-aos="zoom-in">
            <img
              src={getImageSrc(post.imageUrl)}
              alt={post.title}
              className="single-post-image"
              onError={(e) => {
                console.log("Erreur chargement image :", getImageSrc(post.imageUrl));
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}

        <div className="single-post-content" data-aos="fade-up">
          {post.content
            ?.split("\n")
            .filter((line) => line.trim() !== "")
            .map((line, index) => (
              <p key={index}>{line}</p>
            ))}
        </div>

        <div className="post-actions-bar" data-aos="fade-up">
          {token ? (
            <button className="post-like-btn" onClick={handleLike}>
              ❤️ {post.likes || post.likedBy?.length || 0} Likes
            </button>
          ) : (
            <div className="post-auth-warning">
              Connectez-vous pour liker cet article.
            </div>
          )}

          <div className="share-buttons">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
              target="_blank"
              rel="noreferrer"
            >
              Facebook
            </a>

            <a
              href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
              target="_blank"
              rel="noreferrer"
            >
              X / Twitter
            </a>

            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
            </a>

            <a
              href={`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="comments-section" data-aos="fade-up">
        <h3>Commentaires</h3>

        {token ? (
          <form onSubmit={handleCommentSubmit} className="comment-form">
            <input
              type="text"
              placeholder="Votre nom"
              value={commentForm.name}
              onChange={(e) =>
                setCommentForm({ ...commentForm, name: e.target.value })
              }
              required
            />

            <textarea
              placeholder="Votre commentaire"
              value={commentForm.message}
              onChange={(e) =>
                setCommentForm({ ...commentForm, message: e.target.value })
              }
              required
            />

            <button type="submit">Publier</button>
          </form>
        ) : (
          <div className="post-auth-warning">
            Connectez-vous pour commenter cet article.
          </div>
        )}

        <div className="comments-list">
          {post.comments?.length > 0 ? (
            post.comments.map((comment, index) => (
              <div
                key={index}
                className="comment-item"
                data-aos="fade-up"
                data-aos-delay={index * 80}
              >
                <strong>{comment.name}</strong>
                <p>{comment.message}</p>
              </div>
            ))
          ) : (
            <div className="comment-item">
              <strong>Aucun commentaire</strong>
              <p>Soyez le premier à réagir à cet article.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Post;